// src/components/banner.jsx
import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import banner1 from "../img/banner1.jpg";
import banner2 from "../img/banner2.jpg";
import banner3 from "../img/banner3.jpg";

const slides = [
  {
    image: banner1,
   
    title: "Digital Transformation",
    description:
      "Transforms businesses with intelligent digital solutions for faster growth.",
    duration: 6000,
  },
  {
    image: banner2,
   
    title: "Innovative Technology",
    description: "Smart Innovation for a Smarter Tech World.",
    duration: 6000,
  },
  {
    image: banner3,
    title: "Leading Software Development Company",
    description:
      "Delivering high-quality software, IT, SaaS and digital services to help businesses grow faster and smarter.",
    duration: 6000,
  },
];

const Carousel = () => {
  const [current, setCurrent] = useState(0);
  const timeoutRef = useRef(null);
  const pausedRef = useRef(false);

  const goToNext = () => setCurrent((p) => (p + 1) % slides.length);
  const goToPrevious = () =>
    setCurrent((p) => (p - 1 + slides.length) % slides.length);

  // Autoplay logic
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (!pausedRef.current) {
      timeoutRef.current = setTimeout(() => {
        goToNext();
      }, slides[current].duration);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [current]);

  // keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goToPrevious();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const setCurrentAndResetTimer = (index) => {
    setCurrent(index);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  return (
    <div
      className="relative w-full overflow-hidden 
                 h-[650px] sm:h-[750px] md:h-[820px] lg:h-[600px] 
                 max-h-[600px]"
      onMouseEnter={() => {
        pausedRef.current = true;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(
          () => goToNext(),
          slides[current].duration
        );
      }}
    >
      {/* Background Image */}
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={current}
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${slides[current].image})` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="absolute inset-0 flex items-center px-4 sm:px-10 md:px-20 lg:px-40 z-10">
        <div className="max-w-xl text-left flex flex-col">
          <AnimatePresence mode="wait">
            <motion.p
              key={slides[current].welcome}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="text-white text-xs sm:text-sm md:text-base font-medium mb-2 flex items-center"
            >
              {/* <span className="text-blue-400 mr-2 text-xl sm:text-2xl">・</span> */}
              {slides[current].welcome}
            </motion.p>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.h1
              key={slides[current].title}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 drop-shadow-xl leading-snug md:leading-tight"
            >
              {slides[current].title}
            </motion.h1>
          </AnimatePresence>

          {/* Fixed height block for description so button stays in one place */}
          <div className="mb-8 min-h-[60px] sm:min-h-[70px] md:min-h-[80px] lg:min-h-[96px] flex items-start">
            <AnimatePresence mode="wait">
              <motion.p
                key={slides[current].description}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-gray-200 text-sm sm:text-base"
              >
                {slides[current].description}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Contact Us Button (fixed vertical position relative to text block above) */}
          <motion.button
            className="bg-indigo-700 text-white px-6 sm:px-8 py-3 rounded-md font-semibold text-base sm:text-lg hover:bg-indigo-800 transition duration-300 shadow-lg self-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            onClick={() => {
              window.location.href = "/contact-us";
            }}
          >
            Contact Us
          </motion.button>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute right-4 sm:right-6 top-1/2 transform -translate-y-1/2 flex flex-col space-y-3 sm:space-y-4 z-20">
  {/* Previous (←) */}
  <button
    onClick={goToPrevious}
    className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/90 text-gray-800 rounded-full cursor-pointer shadow-md hover:bg-gray-100 transition duration-200"
    aria-label="Previous slide"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4 sm:h-5 sm:w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19l-7-7 7-7"  // ← chevron-left
      />
    </svg>
  </button>

  {/* Next (→) */}
  <button
    onClick={goToNext}
    className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/90 text-gray-800 rounded-full cursor-pointer shadow-md hover:bg-gray-100 transition duration-200"
    aria-label="Next slide"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4 sm:h-5 sm:w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2.5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 5l7 7-7 7"  // → chevron-right
      />
    </svg>
  </button>
</div>


      {/* Progress Bar Navigation */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 w-[80%] max-w-md flex gap-2 z-10">
        {slides.map((slide, index) => (
          <div
            key={index}
            onClick={() => setCurrentAndResetTimer(index)}
            className="flex-1 h-1 bg-white/30 rounded overflow-hidden cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && setCurrentAndResetTimer(index)
            }
            aria-label={`Go to slide ${index + 1}`}
          >
            <motion.div
              className="h-full bg-blue-500"
              initial={{ width: "0%" }}
              animate={{ width: index === current ? "100%" : "0%" }}
              transition={{
                duration: slides[current].duration / 1000,
                ease: "linear",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Carousel;
